import { isLocale, parseLocale } from 'shared/locales'
import type { AppProps } from 'shared/types'
import { Cookies } from 'react-cookie'
import { create } from 'zustand'

const cookies = new Cookies()

export const useAppStore = create<{
  store: AppProps
  // `customize` accepts a partial: setStore deep-merges it into the existing customize (below), so
  // callers (e.g. the user.app sync in AppShell) can update just accentColor/darkMode/button fields.
  setStore: (value: Partial<Omit<AppProps, 'customize'>> & { customize?: Partial<AppProps['customize']> }) => void
  newVersionAvailable: boolean
  setNewVersionAvailable: (value: boolean) => void
  // True once client React has mounted (set in a post-mount effect in `__root`). Read it via
  // `useHydrated()` to gate SSR'd interactive controls (disabled until hydrated) so pre-hydration
  // clicks/changes aren't silently dropped — see hooks/use-hydrated.ts.
  hydrated: boolean
  setHydrated: () => void
  // True once the "people in charge" gate has been shown this session. The gate only appears while
  // the answer is missing, so this latch keeps its step visible (as completed) in the registration
  // indicator after it's answered — otherwise the step count would shift on the next screen.
  peopleStepShown: boolean
  markPeopleStepShown: () => void
  // Company onboarding welcome guide "Close" latch — IN-MEMORY ONLY (not persisted). "Close" hides
  // the guide for the current page session but must reopen it on the next entry (reload / fresh
  // load), so the latch deliberately resets on reload; only "Don't show again" persists (tour=1).
  // `dismissWelcome` is called when the guide is closed; `resetWelcome` (account → "Show welcome
  // guide") clears it so the guide reopens immediately this session.
  welcomeDismissed: boolean
  dismissWelcome: () => void
  resetWelcome: () => void
}>((set) => ({
  store: {
    sdk: false,
    app: undefined,
    clientId:
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('clientId') ?? undefined)
        : undefined,
    userToken:
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('token') ?? undefined)
        : undefined,
    extension: undefined,
    callback: () => undefined,
    routeTo: undefined,
    applicantData: undefined,
    companyData: undefined,
    jobData: undefined,
    profiles: undefined,
    customize: {
      // null = no live SDK `customize` override yet. The accent applier (AccentTheme in __root) reads
      // the per-app color from the SSR-prefetched `getUser` (useSuspenseQuery) and only lets a non-null
      // store value override it — so a reload paints the brand accent from the suspense user, and an SDK
      // `danchiano_render` postMessage still wins live. See accent-theme.ts.
      accentColor: null,
      primaryFont: 'Montserrat, fallback-font, sans-serif',
      secondaryFont: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      taskColor: '#0bd',
      peopleColor: '#9c5',
      contextColor: '#fc4',
      compareColor: '#f60',
      positioningColor: '#4b9faa',
      primaryErrorColor: '#F02849',
      secondaryErrorColor: '#EC5990',
      tourColor: '#357cf0',
      doneColor: '#25C133',
      // From SDK
      disableLogo: false,
      disableReportDescription: false,
      defaultReportPage: 'detailed',
      loginUrl: undefined,
      // accentColor/darkMode/button fields come from user.app via the AppShell sync (see above).
      darkMode: undefined,
      applicantButtonExit: undefined,
      jobButtonExit: undefined,
      enableJobAddApplicantButton: true,
    },
    // Only 'dark'/'light' are explicit choices; anything else (no cookie, or a stale 'theme=undefined'
    // an older build wrote) is 'system'. Without this guard, a 'theme=undefined' cookie reads back as
    // the string "undefined" and is treated as a non-system mode.
    darkMode: ((t) => (t === 'dark' || t === 'light' ? t : 'system'))(cookies.get<string>('theme')),
    resolvedDarkMode: 'light',
    // The client's initial locale MUST match what the server already resolved and rendered, or
    // every translated text node mismatches at hydration (React #418). The industry-standard SSR
    // rule (and the official Lingui + TanStack Start example) is: the server serializes its resolved
    // locale into `<html lang>` and the client seeds its first render from THAT value.
    locale: (() => {
      if (typeof window === 'undefined') {
        const cookieLang = cookies.get('language')
        return isLocale(cookieLang) ? cookieLang : parseLocale({})
      }
      const serverLang = document.documentElement.lang
      if (isLocale(serverLang)) return serverLang
      const langParam = new URLSearchParams(window.location.search).get('lang')
      if (isLocale(langParam)) return langParam
      const cookieLang = cookies.get('language')
      if (isLocale(cookieLang)) return cookieLang
      return parseLocale({ 'accept-language': navigator.language })
    })(),
  },
  setStore: (store) =>
    set((state) => ({
      store: {
        ...state.store,
        ...store,
        customize: {
          ...state.store.customize,
          ...store.customize,
        },
      },
    })),
  newVersionAvailable: false,
  setNewVersionAvailable: (newVersionAvailable) => set({ newVersionAvailable }),
  hydrated: false,
  setHydrated: () => set({ hydrated: true }),
  peopleStepShown: false,
  markPeopleStepShown: () => set({ peopleStepShown: true }),
  // Always starts false — a reload re-arms the guide so "Close" reopens it on the next entry while
  // tour === 0. The welcome dialog only reads this after hydration, so an SSR `false` never mismatches.
  welcomeDismissed: false,
  dismissWelcome: () => set({ welcomeDismissed: true }),
  resetWelcome: () => set({ welcomeDismissed: false }),
}))
